import { Link } from 'react-router-dom';
import { useTranslation, type TranslationKey } from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import type { PromotionalAdsAccessState } from '@/features/promotional-ads/hooks/use-promotional-ads-access';

export type MarketingTab = 'ads' | 'campaigns' | 'kit';

interface MarketingTabDefinition {
  readonly id: MarketingTab;
  readonly to: string;
  readonly labelKey: string;
}

const TABS: readonly MarketingTabDefinition[] = [
  { id: 'ads', to: '/promotions', labelKey: 'admin.offerCampaigns.adsTab' },
  { id: 'campaigns', to: '/promotions/campaigns', labelKey: 'admin.offerCampaigns.campaignsTab' },
  { id: 'kit', to: '/promotions/kit', labelKey: 'admin.promotionKit.tabs.kit' },
];

export interface MarketingTabsProps {
  readonly active: MarketingTab;
  /**
   * The route's gate state (`usePromotionalAdsAccess().state`). The tab bar
   * never reads the gate itself: the route owns the one gate (BUG-P7-1).
   */
  readonly accessState: PromotionalAdsAccessState;
}

/**
 * The Marketing tab bar shared by the Ads, Campaigns and Promotion Kit pages.
 * The Ads tab shows when the Ads page has something to show (authoring,
 * read-only, or the gate's load error with Retry), and always on the Ads page
 * itself. Elsewhere, while the gate settles, an invisible slot of the same
 * size holds its place so Campaigns and Kit do not move when it appears.
 */
export function MarketingTabs({ active, accessState }: MarketingTabsProps) {
  const { t } = useTranslation();
  // On the Ads page the Ads tab is the current page in every state, also
  // while the gate loads (a `hidden` result redirects away).
  const showAds =
    active === 'ads' ||
    accessState === 'authoring' ||
    accessState === 'readOnly' ||
    accessState === 'error';
  // UX-P7-3: while the gate loads elsewhere, an invisible Ads-sized slot keeps
  // Campaigns and Kit where they will be once the Ads tab appears.
  const reserveAds = !showAds && accessState === 'loading';

  return (
    <nav
      aria-label={t('admin.promotionalAds.marketingTabsLabel' as TranslationKey)}
      data-testid="marketing-tabs"
      className="mb-3 flex flex-wrap gap-2"
    >
      {TABS.map((tab) => {
        if (tab.id === 'ads' && !showAds) {
          if (!reserveAds) return null;
          return (
            <Button key={tab.id} asChild variant="outline" size="sm">
              <span
                aria-hidden="true"
                data-testid="marketing-tab-ads-placeholder"
                className="invisible"
              >
                {t(tab.labelKey as TranslationKey)}
              </span>
            </Button>
          );
        }
        const isCurrent = tab.id === active;
        return (
          <Button key={tab.id} asChild variant={isCurrent ? 'default' : 'outline'} size="sm">
            <Link
              to={tab.to}
              aria-current={isCurrent ? 'page' : undefined}
              data-testid={`marketing-tab-${tab.id}`}
            >
              {t(tab.labelKey as TranslationKey)}
            </Link>
          </Button>
        );
      })}
    </nav>
  );
}
